import { Vue, Component } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { Form } from 'element-ui';
import ticketsClient from '@/core/api/ticket-service';
import sitesClient from '@/core/api/sites-service';
import { TicketDTO, TicketUrgency, TicketCategoryDTO } from '@/../client-generator/generated-clients/tickets';
import { SiteCustomerListItemDTO, AutocompleteDTO } from '@/../client-generator/generated-clients/sites';
import { UserProfileDTO } from '@/../client-generator/generated-clients/user';
import { LOADING_CONTENT } from '@/core/store/types/action-types';
import FileUploader from '@/views/components/FileUploader/FileUploader.vue';

import { ticketUtils } from '@/core/utilities/ticket-utilities';

interface ITicketForm {
  categoryId: number | undefined;
  urgency: TicketUrgency | null;
  site: SiteCustomerListItemDTO | null;
  description: string;
  filename: string;
  fileDataUrl: string;
}

@Component({
  components: {
    FileUploader,
  },
  computed: {
    ...mapState(['profile']),
  },
  i18n: {
    messages: {
      it: {
        customerNotActive: 'Cliente non attivo',
        customerNotFound: 'Cliente non trovato',
        ticketNotFound: 'Ticket non trovato',
        emptyMessage: 'Il messaggio è vuoto',
      },
    },
  },
})
export default class TicketForm extends Vue {
  siteCode = '';
  disableSitesSelect = false;

  sites: SiteCustomerListItemDTO[] = [];
  ticketCategories: TicketCategoryDTO[] = [];
  ticketUrgencies: TicketUrgency[] = [];
  profile!: UserProfileDTO;

  ticketUtils = ticketUtils;

  formModel: ITicketForm = {
    categoryId: undefined,
    urgency: null,
    site: null,
    description: '',
    filename: '',
    fileDataUrl: '',
  };

  allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'bmp', 'zip', 'dwg'];

  get formRules() {
    const required = {
      required: true,
      message: this.$t('validation.fieldRequired'),
    };

    return {
      categoryId: required,
      urgency: required,
      site: required,
      description: required,
    };
  }

  async mounted() {
    await this.fetchFormLists();
    this.checkSiteCode();
  }

  checkSiteCode() {
    this.siteCode = this.$route.params.siteCode;
    if (this.siteCode) {
      this.disableSitesSelect = true;
      this.formModel.site = this.sites.find((site) => site.siteCode === this.siteCode) as SiteCustomerListItemDTO;
    }
  }

  async fetchFormLists() {
    try {
      this.$store.dispatch(LOADING_CONTENT, true);
      await Promise.all([this.fetchTicketCategories(), this.fetchTicketUrgencies(), this.fetchSites()]);
    } finally {
      this.$store.dispatch(LOADING_CONTENT, false);
    }
  }

  async fetchSites() {
    try {
      const query = JSON.stringify({ andFilter: false });
      const response = await sitesClient.myList(query);
      this.sites = response.data as SiteCustomerListItemDTO[];
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 204:
          messageError = this.$t('customerNotActive').toString();
          break;
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        case 404:
          messageError = this.$t('customerNotFound').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    }
  }

  async fetchTicketCategories() {
    try {
      const categories = await ticketsClient.categories();
      this.ticketCategories.splice(0, this.ticketCategories.length);
      this.ticketCategories.push(...categories);
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    }
  }

  async fetchTicketUrgencies() {
    this.$store.dispatch(LOADING_CONTENT, true);
    try {
      this.ticketUrgencies = await ticketsClient.urgencies();
    } catch (error) {
      let messageError = 'error';

      switch (error.status) {
        case 401:
          messageError = this.$t('apiErrors.unauthorized').toString();
          break;
        case 403:
          messageError = this.$t('apiErrors.forbidden').toString();
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      this.$message.error(messageError);
    }
  }

  submit() {
    const $form = this.$refs.theForm as Form;

    $form.validate(async (isValid) => {
      if (isValid) {
        this.$store.dispatch(LOADING_CONTENT, true);

        try {
          const site = new AutocompleteDTO({
            id: this.formModel.site?.siteCode,
            value: this.formModel.site?.siteName,
          });

          const ticket = new TicketDTO({
            ticketCategoryId: this.formModel.categoryId,
            urgency: this.formModel.urgency as TicketUrgency,
            description: this.formModel.description,
            companyName: this.profile.profile?.companyName,
            dataUrl: this.formModel.fileDataUrl,
            filename: this.formModel.filename,
            site,
          });

          await ticketsClient.create(ticket);
          this.$router.push({
            name: 'page-tickets',
          });
          this.$message.success(this.$t('ticketCreated').toString());
        } catch (error) {
          let messageError = 'error';

          switch (error.status) {
            case 400:
              messageError = this.$t('emptyMessage').toString();
              break;
            case 401:
              messageError = this.$t('apiErrors.unauthorized').toString();
              break;
            case 403:
              messageError = this.$t('apiErrors.forbidden').toString();
              break;
            case 404:
              messageError = this.$t('ticketNotFound').toString();
              break;
            default:
              messageError = this.$t('apiErrors.serverError').toString();
          }

          this.$message.error(messageError);
        } finally {
          this.$store.dispatch(LOADING_CONTENT, false);
        }
      }
    });
  }

  backToList() {
    this.$router.push({ name: 'page-tickets' });
  }
}
